import { CheckIcon } from "@heroicons/react/24/solid";
import type React from "react";

import { useState, useRef, useEffect } from "react";
import { cn } from "../utils/style";

interface DropdownItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  checked?: boolean;
}

interface DropdownButtonProps {
  label: string;
  items: DropdownItem[];
  icon?: React.ReactNode;
  className?: string;
  itemClassName?: string;
  align?: "left" | "right";
  closeOnItemClick?: boolean;
  disabled?: boolean;
}

export function DropdownButton({
  label,
  items,
  icon,
  className,
  itemClassName,
  align,
  closeOnItemClick = true,
  disabled,
}: DropdownButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleItemClick = (onClick: () => void) => {
    onClick();
    if (closeOnItemClick) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Dropdown Button */}
      <button
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          "inline-flex items-center gap-2 px-4 py-2 font-medium text-sm bg-white text-gray-800 rounded-lg",
          "enabled:hover:bg-gray-100/90 disabled:opacity-60 disabled:cursor-not-allowed",
          "border border-gray-300",
          "focus:outline-none focus:border focus:border-blue-500",
          "transition-colors duration-200",
          className,
        )}
      >
        {icon && <span className="w-4 h-4">{icon}</span>}
        {label}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 w-48 bg-white text-popover-foreground rounded-lg shadow-lg border border-border z-50 overflow-hidden",
            "animate-in fade-in-75 zoom-in-75",
            align === "right"
              ? "right-0 origin-top-right"
              : "left-0 origin-top-left",
          )}
        >
          <div className="max-h-[40vh] overflow-y-auto p-2">
            {items.map((item, index) => (
              <button
                key={index}
                onClick={() => handleItemClick(item.onClick)}
                className={cn(
                  "w-full p-2 text-left text-sm flex items-center gap-2 transition-colors duration-150 rounded-md hover:bg-gray-100",
                  itemClassName,
                )}
              >
                {item.icon && <span className="w-4 h-4">{item.icon}</span>}
                {item.label}
                {item.checked && <CheckIcon className="size-4 ml-auto" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
